import { Briefcase, Minus, Plus, ShoppingBag, Store, User } from 'lucide-react';
import { type FocusEvent, useEffect } from 'react';
import { type UseFormReturn, useWatch } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { totalQty, type UiValues } from './_shared';
import { ColorCombobox } from './color-combobox';
import { StyleCombobox, type StylePick } from './style-combobox';
import { type SizeBand, useStyleBands } from './use-style-bands';

type FormProp = { form: UseFormReturn<UiValues> };

const SECTION = 'space-y-4';
const SECTION_LABEL = 'text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground';
const INPUT_CLASS = 'h-11 text-base';

/**
 * Proposal C form layout — single vertical column. Scenario tabs
 * (rendered above, in screen-print-quote.tsx) are the primary UX
 * affordance; the form itself stays linear per the Claude Design
 * reference. A's 2-col inner grid was replaced because it competes
 * visually with the tab strip + result rail.
 */
export function QuoteForm({ form }: FormProp) {
  return (
    <div className="space-y-8">
      <PricingModeSection form={form} />
      <GarmentSection form={form} />
      <QuantitiesSection form={form} />
      <LocationsSection form={form} />
      <RushSection form={form} />
      <DiscountSection form={form} />
    </div>
  );
}

// ─── Garment ────────────────────────────────────────────────────────────────

function GarmentSection({ form }: FormProp) {
  const source = useWatch({ control: form.control, name: 'garmentSource' });

  return (
    <section className={SECTION}>
      <h2 className={SECTION_LABEL}>Garment</h2>
      <ToggleGroup
        type="single"
        value={source}
        onValueChange={(v) => {
          if (!v) return;
          form.setValue('garmentSource', v as UiValues['garmentSource'], { shouldDirty: true });
        }}
        className="grid w-full grid-cols-3 gap-2"
      >
        <ToggleGroupItem
          value="we_provide_by_style"
          aria-label="SS Activewear"
          className="h-auto flex-col gap-1 rounded-md border bg-card py-3 shadow-xs data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-foreground data-[state=on]:ring-1 data-[state=on]:ring-primary"
        >
          <ShoppingBag className="size-4" />
          <span className="text-sm font-medium">SS Activewear</span>
        </ToggleGroupItem>
        <ToggleGroupItem
          value="we_provide"
          aria-label="We provide"
          className="h-auto flex-col gap-1 rounded-md border bg-card py-3 shadow-xs data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-foreground data-[state=on]:ring-1 data-[state=on]:ring-primary"
        >
          <Store className="size-4" />
          <span className="text-sm font-medium">We provide</span>
        </ToggleGroupItem>
        <ToggleGroupItem
          value="customer_provides"
          aria-label="Customer brings"
          className="h-auto flex-col gap-1 rounded-md border bg-card py-3 shadow-xs data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-foreground data-[state=on]:ring-1 data-[state=on]:ring-primary"
        >
          <User className="size-4" />
          <span className="text-sm font-medium">Customer brings</span>
        </ToggleGroupItem>
      </ToggleGroup>

      {source === 'we_provide_by_style' && <GarmentByStyle form={form} />}
      {source === 'we_provide' && <GarmentWeProvide form={form} />}
      {source === 'customer_provides' && <GarmentCustomerBrings form={form} />}
    </section>
  );
}

function GarmentByStyle({ form }: FormProp) {
  const style = useWatch({ control: form.control, name: 'style' });
  const colorName = useWatch({ control: form.control, name: 'colorName' });

  function handleStylePick(pick: StylePick) {
    form.setValue('style', pick.style, { shouldDirty: true });
    // Clear color — the new style may not have the previously-picked color
    // as a variant, and the color picker will surface only what's actually
    // available. Saves the user a "color not found" error post-switch.
    if (colorName) form.setValue('colorName', '', { shouldDirty: true });
  }

  return (
    <div className="grid gap-3 sm:grid-cols-[1.5fr_1fr]">
      <div className="space-y-1.5">
        <Label>Style</Label>
        <StyleCombobox value={style} onPick={handleStylePick} />
      </div>
      <div className="space-y-1.5">
        <Label>Color</Label>
        <ColorCombobox
          style={style}
          value={colorName}
          onPick={(c) => form.setValue('colorName', c, { shouldDirty: true })}
        />
      </div>
      <p className="text-xs text-muted-foreground sm:col-span-2">
        Search the SS Activewear catalog, pick a Quick pick, or type a brand + style.
      </p>
    </div>
  );
}

function GarmentWeProvide({ form }: FormProp) {
  const blankCost = useWatch({ control: form.control, name: 'blankCostEach' });
  const color = useWatch({ control: form.control, name: 'garmentColor' });
  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_1fr]">
      <div className="space-y-1.5">
        <Label htmlFor="sp-blank-cost">Blank cost / unit</Label>
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            $
          </span>
          <Input
            id="sp-blank-cost"
            className={`${INPUT_CLASS} pl-7`}
            inputMode="decimal"
            type="text"
            placeholder="0.00"
            value={Number.isFinite(blankCost) ? String(blankCost) : ''}
            onFocus={selectOnFocus}
            onChange={(e) => {
              const v = Number.parseFloat(e.target.value);
              form.setValue('blankCostEach', Number.isFinite(v) ? v : 0, { shouldDirty: true });
            }}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Garment color</Label>
        <ToggleGroup
          type="single"
          value={color}
          onValueChange={(v) => {
            if (!v) return;
            form.setValue('garmentColor', v as UiValues['garmentColor'], { shouldDirty: true });
          }}
          className="grid w-full grid-cols-2 gap-2"
        >
          <ToggleGroupItem
            value="white"
            className="h-11 rounded-md border bg-card shadow-xs data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:ring-1 data-[state=on]:ring-primary"
          >
            Light / White
          </ToggleGroupItem>
          <ToggleGroupItem
            value="colored"
            className="h-11 rounded-md border bg-card shadow-xs data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:ring-1 data-[state=on]:ring-primary"
          >
            Colored / Dark
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
    </div>
  );
}

function GarmentCustomerBrings({ form }: FormProp) {
  const color = useWatch({ control: form.control, name: 'garmentColor' });
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label>Garment color</Label>
        <ToggleGroup
          type="single"
          value={color}
          onValueChange={(v) => {
            if (!v) return;
            form.setValue('garmentColor', v as UiValues['garmentColor'], { shouldDirty: true });
          }}
          className="grid w-full grid-cols-2 gap-2"
        >
          <ToggleGroupItem
            value="white"
            className="h-11 rounded-md border bg-card shadow-xs data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:ring-1 data-[state=on]:ring-primary"
          >
            Light / White
          </ToggleGroupItem>
          <ToggleGroupItem
            value="colored"
            className="h-11 rounded-md border bg-card shadow-xs data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:ring-1 data-[state=on]:ring-primary"
          >
            Colored / Dark
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      <p className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-900 dark:text-amber-200">
        Customer-supplied garments — blank cost excluded. We are not responsible for damage during
        printing. Customer confirms garments are suitable for screen print.
      </p>
    </div>
  );
}

// ─── Quantities ─────────────────────────────────────────────────────────────

type QtyName = 'sxl' | 'xxl' | 'xxxl' | 'xxxxl' | 'xxxxxl';

const BAND_ROWS: {
  band: SizeBand;
  name: QtyName;
  label: string;
  hint?: string;
}[] = [
  { band: 'S-XL', name: 'sxl', label: 'S–XL' },
  { band: '2XL', name: 'xxl', label: '2XL', hint: '+$1.50' },
  { band: '3XL', name: 'xxxl', label: '3XL', hint: '+$2.50' },
  // 4XL / 5XL have no flat upcharge hint — SS-priced only (engine throws
  // for non-SS paths, so we only render these when the picked style
  // actually has those bands at SS).
  { band: '4XL', name: 'xxxxl', label: '4XL' },
  { band: '5XL', name: 'xxxxxl', label: '5XL' },
];

function QuantitiesSection({ form }: FormProp) {
  const sxl = useWatch({ control: form.control, name: 'sxl' });
  const xxl = useWatch({ control: form.control, name: 'xxl' });
  const xxxl = useWatch({ control: form.control, name: 'xxxl' });
  const xxxxl = useWatch({ control: form.control, name: 'xxxxl' });
  const xxxxxl = useWatch({ control: form.control, name: 'xxxxxl' });
  const total = totalQty({ sxl, xxl, xxxl, xxxxl, xxxxxl });

  const garmentSource = useWatch({ control: form.control, name: 'garmentSource' });
  const style = useWatch({ control: form.control, name: 'style' });
  // Only consult SS for band availability when the SS Activewear source
  // is active. Other sources fall back to the 3-bucket default since
  // the engine refuses 4XL/5XL without per-size data there.
  const ssBands = useStyleBands(garmentSource === 'we_provide_by_style' ? style : '');
  const baseRows = chooseVisibleRows(garmentSource, ssBands);
  // Always surface a band that currently holds a non-zero qty — otherwise
  // a parse result that sets `xxxxl: 25` against an unresolved style would
  // hide the input AND the zero-out effect below would silently drop the
  // value before the owner sees it. Source-of-truth is what's actually in the
  // form.
  const baseNames = new Set(baseRows.map((r) => r.name));
  const extraRows = BAND_ROWS.filter(
    (r) =>
      !baseNames.has(r.name) &&
      ((r.name === 'xxl' && xxl > 0) ||
        (r.name === 'xxxl' && xxxl > 0) ||
        (r.name === 'xxxxl' && xxxxl > 0) ||
        (r.name === 'xxxxxl' && xxxxxl > 0)),
  );
  const visibleRows = [...baseRows, ...extraRows];

  // Zero out any band whose input is currently hidden. Skips bands that
  // the extra-rows logic above just revealed, so explicit user / parse
  // input survives. Without this, a user who enters 4XL qty against an
  // SS style that carries 4XL, then switches garment source or picks a
  // style without that band, would send the stale qty and hit
  // `oversized_blank_cost_required` from the engine even though their
  // visible form shows no 4XL row.
  const visibleKey = visibleRows.map((r) => r.name).join(',');
  useEffect(() => {
    const visible = new Set(visibleKey.split(','));
    const opts = { shouldDirty: true, shouldValidate: false } as const;
    if (!visible.has('xxl') && xxl !== 0) form.setValue('xxl', 0, opts);
    if (!visible.has('xxxl') && xxxl !== 0) form.setValue('xxxl', 0, opts);
    if (!visible.has('xxxxl') && xxxxl !== 0) form.setValue('xxxxl', 0, opts);
    if (!visible.has('xxxxxl') && xxxxxl !== 0) form.setValue('xxxxxl', 0, opts);
  }, [visibleKey, xxl, xxxl, xxxxl, xxxxxl, form]);

  return (
    <section className={SECTION}>
      <div className="flex items-baseline justify-between">
        <h2 className={SECTION_LABEL}>Quantities</h2>
        {total > 0 && (
          <span className="text-xs text-muted-foreground tabular-nums">{total} total</span>
        )}
      </div>
      <div
        className={cn(
          'grid grid-cols-1 gap-3',
          visibleRows.length === 3 && 'sm:grid-cols-3',
          visibleRows.length === 4 && 'sm:grid-cols-2 lg:grid-cols-4',
          visibleRows.length >= 5 && 'sm:grid-cols-3 lg:grid-cols-5',
        )}
      >
        {visibleRows.map((row, i) => (
          <QtyInput
            key={row.name}
            form={form}
            name={row.name}
            label={row.label}
            hint={row.hint}
            autoFocus={i === 0}
          />
        ))}
      </div>
    </section>
  );
}

function chooseVisibleRows(
  garmentSource: UiValues['garmentSource'],
  ssBands: SizeBand[] | undefined,
): (typeof BAND_ROWS)[number][] {
  if (garmentSource === 'we_provide_by_style' && ssBands) {
    return BAND_ROWS.filter((r) => ssBands.includes(r.band));
  }
  // Non-SS path or SS path while the bands list is still loading: show
  // the classic 3-bucket default. The engine will 422 if 4XL/5XL sneaks
  // in on non-SS paths.
  return BAND_ROWS.slice(0, 3);
}

function QtyInput({
  form,
  name,
  label,
  hint,
  autoFocus,
}: FormProp & {
  name: QtyName;
  label: string;
  hint?: string;
  autoFocus?: boolean;
}) {
  const value = useWatch({ control: form.control, name });
  const id = `sp-${name}`;
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between">
        <Label htmlFor={id}>{label}</Label>
        {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
      </div>
      <Input
        id={id}
        className={INPUT_CLASS}
        inputMode="numeric"
        type="text"
        pattern="[0-9]*"
        value={Number.isFinite(value) ? String(value) : ''}
        onFocus={selectOnFocus}
        onChange={(e) => {
          const digits = e.target.value.replace(/[^0-9]/g, '');
          form.setValue(name, digits === '' ? 0 : Number.parseInt(digits, 10), {
            shouldDirty: true,
          });
        }}
        autoFocus={autoFocus}
      />
    </div>
  );
}

// ─── Locations ──────────────────────────────────────────────────────────────

function LocationsSection({ form }: FormProp) {
  const active = useWatch({
    control: form.control,
    name: ['frontEnabled', 'backEnabled', 'sleeveEnabled'],
  });
  const [front, back, sleeve] = active;
  const activeValues = [front && 'front', back && 'back', sleeve && 'sleeve'].filter(
    Boolean,
  ) as string[];

  return (
    <section className={SECTION}>
      <h2 className={SECTION_LABEL}>Print locations</h2>
      <ToggleGroup
        type="multiple"
        value={activeValues}
        onValueChange={(values) => {
          form.setValue('frontEnabled', values.includes('front'), { shouldDirty: true });
          form.setValue('backEnabled', values.includes('back'), { shouldDirty: true });
          form.setValue('sleeveEnabled', values.includes('sleeve'), { shouldDirty: true });
        }}
        className="grid w-full grid-cols-3 gap-2"
      >
        <ToggleGroupItem
          value="front"
          className="h-11 rounded-md border bg-card shadow-xs data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-foreground data-[state=on]:ring-1 data-[state=on]:ring-primary"
        >
          Front
        </ToggleGroupItem>
        <ToggleGroupItem
          value="back"
          className="h-11 rounded-md border bg-card shadow-xs data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-foreground data-[state=on]:ring-1 data-[state=on]:ring-primary"
        >
          Back
        </ToggleGroupItem>
        <ToggleGroupItem
          value="sleeve"
          className="h-11 rounded-md border bg-card shadow-xs data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-foreground data-[state=on]:ring-1 data-[state=on]:ring-primary"
        >
          Sleeve
        </ToggleGroupItem>
      </ToggleGroup>

      <div className="space-y-2">
        {front && <ColorStepper form={form} name="frontColors" label="Front · colors" />}
        {back && <ColorStepper form={form} name="backColors" label="Back · colors" />}
        {sleeve && <ColorStepper form={form} name="sleeveColors" label="Sleeve · colors" />}
      </div>
    </section>
  );
}

function ColorStepper({
  form,
  name,
  label,
}: FormProp & { name: 'frontColors' | 'backColors' | 'sleeveColors'; label: string }) {
  const value = useWatch({ control: form.control, name });
  const clamp = (n: number) => Math.max(1, Math.min(8, n));
  const set = (n: number) => form.setValue(name, clamp(n), { shouldDirty: true });

  return (
    <div className="flex items-center justify-between rounded-md border bg-background px-3 py-2">
      <span className="text-sm">{label}</span>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          onClick={() => set(value - 1)}
          disabled={value <= 1}
          aria-label={`Decrease ${label}`}
        >
          <Minus />
        </Button>
        <span className="w-8 text-center text-sm font-medium tabular-nums">{value}</span>
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          onClick={() => set(value + 1)}
          disabled={value >= 8}
          aria-label={`Increase ${label}`}
        >
          <Plus />
        </Button>
      </div>
    </div>
  );
}

// ─── Pricing mode ───────────────────────────────────────────────────────────

/**
 * Mode (B2C vs B2B) is a top-level input, not an "advanced" toggle.
 * Switching it changes every downstream number — tier floors, print
 * rates, discount baseline. Promoting it to the top of the form makes
 * that impact legible and prevents users from quoting a B2B job at B2C
 * retail by accident (the buried-Advanced-section failure mode).
 */
function PricingModeSection({ form }: FormProp) {
  const mode = useWatch({ control: form.control, name: 'mode' });
  return (
    <section className={SECTION}>
      <div className="flex items-baseline justify-between">
        <h2 className={SECTION_LABEL}>Pricing mode</h2>
        <span className="text-xs text-muted-foreground">
          {mode === 'b2b'
            ? 'Wholesale tiers · below-floor guarded'
            : 'Retail tiers · walk-in / Etsy'}
        </span>
      </div>
      <ToggleGroup
        type="single"
        value={mode}
        onValueChange={(v) => {
          if (!v) return;
          form.setValue('mode', v as UiValues['mode'], { shouldDirty: true });
        }}
        className="grid w-full grid-cols-2 gap-2"
      >
        <ToggleGroupItem
          value="b2c"
          aria-label="B2C — retail"
          className="h-auto flex-col gap-1 rounded-md border bg-card py-3 shadow-xs data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-foreground data-[state=on]:ring-1 data-[state=on]:ring-primary"
        >
          <User className="size-4" />
          <span className="text-sm font-medium">B2C — retail</span>
        </ToggleGroupItem>
        <ToggleGroupItem
          value="b2b"
          aria-label="B2B — wholesale"
          className="h-auto flex-col gap-1 rounded-md border bg-card py-3 shadow-xs data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-foreground data-[state=on]:ring-1 data-[state=on]:ring-primary"
        >
          <Briefcase className="size-4" />
          <span className="text-sm font-medium">B2B — wholesale</span>
        </ToggleGroupItem>
      </ToggleGroup>
    </section>
  );
}

// ─── Rush ───────────────────────────────────────────────────────────────────

/**
 * Rush surcharge — applied post-discount to per-unit sell. Multipliers
 * are PROVISIONAL (none / +25% / +50%); owner can retune in the
 * engine's RUSH_MULTIPLIERS table without touching this UI.
 */
function RushSection({ form }: FormProp) {
  const rush = useWatch({ control: form.control, name: 'rush' });
  return (
    <section className={SECTION}>
      <div className="flex items-baseline justify-between">
        <h2 className={SECTION_LABEL}>Rush</h2>
        <span className="text-xs text-muted-foreground">
          {rush === 'none'
            ? 'Standard turnaround'
            : rush === 'rush'
              ? '+25% surcharge'
              : '+50% surcharge'}
        </span>
      </div>
      <ToggleGroup
        type="single"
        value={rush}
        onValueChange={(v) => {
          if (!v) return;
          form.setValue('rush', v as UiValues['rush'], { shouldDirty: true });
        }}
        className="grid w-full grid-cols-3 gap-2"
      >
        <ToggleGroupItem
          value="none"
          aria-label="Standard turnaround"
          className="h-11 rounded-md border bg-card shadow-xs data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:ring-1 data-[state=on]:ring-primary"
        >
          Standard
        </ToggleGroupItem>
        <ToggleGroupItem
          value="rush"
          aria-label="Rush + 25 percent"
          className="h-11 rounded-md border bg-card shadow-xs data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:ring-1 data-[state=on]:ring-primary"
        >
          Rush · +25%
        </ToggleGroupItem>
        <ToggleGroupItem
          value="hot_rush"
          aria-label="Hot rush + 50 percent"
          className="h-11 rounded-md border bg-card shadow-xs data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:ring-1 data-[state=on]:ring-primary"
        >
          Hot · +50%
        </ToggleGroupItem>
      </ToggleGroup>
    </section>
  );
}

// ─── Discount (inline) ──────────────────────────────────────────────────────

function DiscountSection({ form }: FormProp) {
  const discount = useWatch({ control: form.control, name: 'discountPct' });
  const isSet = discount > 0;

  return (
    <section className={SECTION}>
      <div className="flex items-baseline justify-between">
        <h2 className={SECTION_LABEL}>Discount</h2>
        <span className="text-xs text-muted-foreground">Applied before floor</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-[120px]">
          <Input
            id="sp-discount"
            aria-label="Percent off retail"
            className={`${INPUT_CLASS} pr-8`}
            inputMode="decimal"
            type="text"
            placeholder="0"
            value={Number.isFinite(discount) ? String(discount) : ''}
            onFocus={selectOnFocus}
            onChange={(e) => {
              const cleaned = e.target.value.replace(/[^0-9.]/g, '');
              const n = Number.parseFloat(cleaned);
              form.setValue('discountPct', Number.isFinite(n) ? Math.max(0, Math.min(50, n)) : 0, {
                shouldDirty: true,
              });
            }}
          />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            %
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[5, 10, 15, 20].map((n) => (
            <Button
              key={n}
              type="button"
              variant={discount === n ? 'default' : 'outline'}
              size="sm"
              className="h-9 px-2.5 text-xs"
              onClick={() =>
                form.setValue('discountPct', n, { shouldDirty: true, shouldValidate: false })
              }
            >
              {n}%
            </Button>
          ))}
          {isSet && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9 px-2.5 text-xs text-muted-foreground"
              onClick={() =>
                form.setValue('discountPct', 0, { shouldDirty: true, shouldValidate: false })
              }
            >
              Clear
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── Utils ──────────────────────────────────────────────────────────────────

function selectOnFocus(e: FocusEvent<HTMLInputElement>) {
  e.currentTarget.select();
}
